import { Fragment, useMemo, useState } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import arrowLeft from '~/assets/icons/arrow_left.svg';
import arrowRight from '~/assets/icons/arrow_right.svg';
import star from '~/assets/icons/four_final_star.svg';
import { playSound, Sound } from '~/assets/sounds';
import { ClothesImage } from '~/components/collections/ClothesImage';
import { CollectionImage } from '~/components/collections/CollectionImage';
import { ImgIcon } from '~/components/ui/ImgIcon';
import { Text } from '~/components/ui/Text';
import { rage, useObservable } from '~/rage';
import { isClothesItem, ParseRewardValueTypeMap, RewardType } from '~/shared';
import cardSoft from '../../assets/images/cases/card_soft.png';
import {
	StyledButton,
	StyledContainer,
	StyledContentWrapper,
	StyledExpContainer,
	StyledImageContainer,
	StyledShadow,
	StyledTitleContainer,
} from './ShopOpenCaseItem.styled';

const ShopOpenCaseItem = ({
	type,
	id,
	color,
	name,
}: {
	type: RewardType;
	id: string[];
	color: string;
	name: string;
}) => {
	const [activeId, setActiveId] = useState(id[0]);
	const lootboxes = useObservable(rage.cases.getLootboxes());
	const lootbox = useMemo(() => {
		return lootboxes.find(
			(caseItem) => caseItem.donateItemId === id[0] || caseItem.id === id[0],
		);
	}, [lootboxes, id]);

	const handlePrev = () => {
		setActiveId((prev) => {
			const currentIndex = id.indexOf(prev);
			const prevIndex = currentIndex === 0 ? id.length - 1 : currentIndex - 1;
			return id[prevIndex];
		});
	};
	const handleNext = () => {
		setActiveId((prev) => {
			const currentIndex = id.indexOf(prev);
			const nextIndex = currentIndex === id.length - 1 ? 0 : currentIndex + 1;
			return id[nextIndex];
		});
	};

	const reward = rage.cases.getRewardById(id[0]);
	const amount = useMemo(() => {
		if (type === RewardType.EXP) {
			return (reward.value as ParseRewardValueTypeMap[RewardType.EXP]) ?? 0;
		}
		if (type === RewardType.MONEY) {
			return (reward.value as ParseRewardValueTypeMap[RewardType.MONEY]) ?? 0;
		}

		return 0;
	}, [reward]);

	return (
		<StyledContainer>
			<StyledShadow style={{ backgroundColor: color }} />
			<StyledTitleContainer $color={color}>
				<div className="dot" />
				<Text>{name}</Text>
			</StyledTitleContainer>
			<TransitionGroup>
				<CSSTransition key={activeId} timeout={300} classNames="fade">
					<StyledContentWrapper>
						<StyledImageContainer $type={type}>
							{type === RewardType.VEHICLE && (
								<CollectionImage
									collection={'vehicles'}
									id={activeId}
									size={120}
								/>
							)}
							{type === RewardType.ITEM && isClothesItem(activeId) && (
								<ClothesImage id={activeId} size={140} />
							)}
							{type === RewardType.ITEM && (
								<CollectionImage
									collection={'items'}
									id={activeId}
									size={140}
								/>
							)}
							{type === RewardType.MONEY && (
								<img src={cardSoft} width={140} height={140} />
							)}
							{type === RewardType.EXP && (
								<StyledExpContainer>
									<ImgIcon icon={star} width={30} height={30} color="#FFB500" />
									<Text>{amount.toLocaleString('ru-RU')}</Text>
								</StyledExpContainer>
							)}
							{type === RewardType.LOOTBOX && (
								<CollectionImage
									collection={'cases'}
									id={lootbox?.id ?? activeId ?? ''}
									size={250}
								/>
							)}
						</StyledImageContainer>
					</StyledContentWrapper>
				</CSSTransition>
			</TransitionGroup>
			{id.length > 1 && (
				<Fragment>
					<StyledButton
						style={{ left: 20 }}
						onMouseEnter={() => playSound(Sound.LinkCursor)}
						onClick={() => {
							handlePrev();
							playSound(Sound.ClickButton);
						}}
					>
						<img src={arrowLeft} alt="arrow-left" />
					</StyledButton>
					<StyledButton
						style={{ right: 20 }}
						onMouseEnter={() => playSound(Sound.LinkCursor)}
						onClick={() => {
							handleNext();
							playSound(Sound.ClickButton);
						}}
					>
						<img src={arrowRight} alt="arrow-right" />
					</StyledButton>
				</Fragment>
			)}
		</StyledContainer>
	);
};

export { ShopOpenCaseItem };
